import type { PrintTypes } from 'print-js';

import type { BaseResponse } from 'shared/models/common';
import type { Document } from 'shared/models/document';
import type { Folder } from 'shared/models/folder';
import axios from 'services';
import { downloadDocumentEndpoint } from './api';

export function transformResponse<T>(response: BaseResponse<T>) {
  return response.Data;
}

export function isFolder(entity: Document | Folder): entity is Folder {
  return 'Documents' in entity;
}

export function extractDocuments(entities: (Folder | Document)[]) {
  const documents: Document[] = [];

  for (let entity of entities) {
    if (isFolder(entity)) {
      documents.push(
        ...extractDocuments([...entity.Folders, ...entity.Documents])
      );
    } else {
      documents.push(entity);
    }
  }

  return documents;
}

export function convertDocumentExtensionToPrintType(
  extension: string
): PrintTypes | null {
  switch (extension) {
    case '.jpg':
    case '.png':
    case '.svg':
    case '.gif':
      return 'image';
    case '.pdf':
      return 'pdf';
    default:
      return null;
  }
}

export function getDocumentsPrintType(documents: Document[]) {
  let documentsTypes = documents.map((document) =>
    convertDocumentExtensionToPrintType(document.Extension)
  );

  const firstDocType = documentsTypes[0];

  let isValidTypes = !!(
    firstDocType &&
    !documentsTypes.some((documentType) => {
      return documentType !== firstDocType;
    })
  );

  return isValidTypes ? firstDocType : null;
}

function buildNestedPathsWithNewFolderName(
  folder: Folder,
  newPathName: string
) {
  const paths: string[] = [];
  const entities = [...folder.Folders, ...folder.Documents];

  for (let entity of entities) {
    if (isFolder(entity)) {
      const newFolderName = `${newPathName}/${entity.Name}`;

      paths.push(newFolderName);
      paths.push(...buildNestedPathsWithNewFolderName(entity, newFolderName));
    } else {
      const newDocumentName = `${newPathName}/${entity.Name}`;
      paths.push(newDocumentName);
    }
  }

  return paths;
}

export function getNestedPathGreaterThanOrEqualLimit(
  folder: Folder,
  options: { newPathName: string; limit: number }
) {
  const { limit, newPathName } = options;
  const paths = buildNestedPathsWithNewFolderName(folder, newPathName);

  return paths.find((path) => path.length >= limit) ?? null;
}

export const warnings = {
  entityNameExceedLimit: (limit: number, entityNameLength: number) =>
    `Tiedoston/kansion nimen pituus ${entityNameLength} ylittää sallitun maksimimäärän (${limit}). Lyhennä tiedoston/kansion nimeä.`,
  entityPathEqualLimit: (limit: number) =>
    `Tiedostopolun maksimimerkkimäärä on ${limit} merkkiä. Annetulla kansionimellä maksimimerkkimäärä täyttyy eikä tiedostoja kansioon voi lisätä. Lyhennä kansionimeä tallentaaksesi.`,
  entityPathExceedLimit: (limit: number, entityPathLength: number) =>
    `Tiedostopolun maksimimerkkimäärä on ${limit} merkkiä. Nyt annetulla tiedostonimellä merkkimääräksi muodostuu ${entityPathLength}. Lyhennä tiedostonimeä tai aiempaa kansion nimeä tallentaaksesi.`,
  folderIsNotEmpty:
    'Kansiota, jossa on tiedostoja ei voi poistaa. Poistaaksesi kansion, poista ensin sen sisällä olevat asiakirjat.',
  documentNameHasUnavailableSymbols:
    'Tiedostonimessä on erikoismerkki jota ei hyväksytä. Muuta tiedoston nimi ja koita tallennusta uudelleen.',
};
export function countTotalEntities(entities: (Document | Folder)[]) {
  let totalCount = 0;

  for (let entity of entities) {
    if (isFolder(entity)) {
      let innerEntities = [...entity.Folders, ...entity.Documents];

      totalCount += countTotalEntities(innerEntities);
    } else {
      totalCount++;
    }
  }

  return totalCount;
}

const MAINTENANCE_PORTAL_NAME = 'Maintenance';

export function isMaintenance(portalName: string) {
  return portalName === MAINTENANCE_PORTAL_NAME;
}

export async function downloadDocument(params: {
  cooperativeId: string;
  fileUrl: string;
  portalName: string;
}) {
  const { cooperativeId, fileUrl, portalName } = params;

  const res = await axios.get<Blob>(
    downloadDocumentEndpoint({ cooperativeId, portalName }),
    { params: { fileUrl: encodeURIComponent(fileUrl) }, responseType: 'blob' }
  );

  return res.data;
}
